'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Search, Bed, Home, DollarSign, Sofa, ArrowRight, Dog } from 'lucide-react';
import Image from 'next/image';

export default function HeroResidential() {
  const router = useRouter();
  const [bedrooms, setBedrooms] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [furnishing, setFurnishing] = useState('');
  const [propertyType, setPropertyType] = useState('');

  const handleSearch = () => {
    const params = new URLSearchParams();

    if (propertyType) params.append('property_type', propertyType);
    if (bedrooms) params.append('bedrooms', bedrooms);

    // Convert price range to min_price and max_price
    if (priceRange) {
      if (priceRange.includes('-')) {
        const [min, max] = priceRange.split('-');
        params.append('min_price', min);
        if (max && !max.includes('+')) {
          params.append('max_price', max);
        }
      } else if (priceRange.includes('+')) {
        params.append('min_price', priceRange.replace('+', ''));
      }
    }

    // Convert short furnishing to full name
    if (furnishing) {
      const furnishingMap: {[key: string]: string} = {
        'fully': 'Fully Furnished',
        'semi': 'Semi Furnished',
        'bare': 'Bare'
      };
      params.append('furnishing', furnishingMap[furnishing] || furnishing);
    }

    params.append('listing_type', 'rent');

    router.push(`/properties?${params.toString()}`);
  };

  return (
    <section className="relative flex flex-col">
      {/* Hero Image Background */}
      <div className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center py-16 md:py-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <Image
            src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1920&q=80"
            alt="Modern Residential Living Spaces"
            fill
            className="object-cover"
            priority
            quality={95}
          />
          {/* Warm overlay for residential */}
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-900/60 via-cyan-800/50 to-black/70"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center mb-12">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-white font-medium">Residential Brokerage • Full Lease Support</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Helping You Find the Right Home to Rent
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
              From city condos to family houses, our team assists you from inquiry to move-in.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-12">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">Condos</div>
                <div className="text-sm text-white/70">Modern Units</div>
              </div>
              <div className="text-center border-x border-white/20">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">Houses</div>
                <div className="text-sm text-white/70">Family Spaces</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">Townhouses</div>
                <div className="text-sm text-white/70">Private Living</div>
              </div>
            </div>
          </div>

          {/* Search Section - Integrated */}
          <div id="search-section" className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                What Type of Home Are You Looking For?
              </h2>
              <p className="text-sm md:text-base text-gray-600">
                Tell us your budget and preferred area — we'll match you with available options
              </p>
            </div>

            {/* Search Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              {/* Property Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Home className="w-4 h-4 inline mr-2" />
                  Property Type
                </label>
                <select
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value)}
                  className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-white text-gray-900 font-medium focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 focus:outline-none transition-all cursor-pointer"
                >
                  <option value="">All Types</option>
                  <option value="condo">Condo</option>
                  <option value="house">House</option>
                  <option value="townhouse">Townhouse</option>
                </select>
              </div>

              {/* Bedroom Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Bed className="w-4 h-4 inline mr-2" />
                  Bedrooms
                </label>
                <select
                  value={bedrooms}
                  onChange={(e) => setBedrooms(e.target.value)}
                  className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-white text-gray-900 font-medium focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 focus:outline-none transition-all cursor-pointer"
                >
                  <option value="">All Bedrooms</option>
                  <option value="studio">Studio</option>
                  <option value="1">1 BR</option>
                  <option value="2">2 BR</option>
                  <option value="3">3 BR</option>
                  <option value="4">4 BR+</option>
                </select>
              </div>

              {/* Price Range */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <DollarSign className="w-4 h-4 inline mr-2" />
                  Monthly Rent
                </label>
                <select
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-white text-gray-900 font-medium focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 focus:outline-none transition-all cursor-pointer"
                >
                  <option value="">All Prices</option>
                  <option value="0-30000">₱18,000 - ₱30,000</option>
                  <option value="30000-50000">₱30,000 - ₱50,000</option>
                  <option value="50000-80000">₱50,000 - ₱80,000</option>
                  <option value="80000-120000">₱80,000 - ₱120,000</option>
                  <option value="120000+">₱120,000+</option>
                </select>
              </div>

              {/* Furnishing */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Sofa className="w-4 h-4 inline mr-2" />
                  Furnishing
                </label>
                <select
                  value={furnishing}
                  onChange={(e) => setFurnishing(e.target.value)}
                  className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl bg-white text-gray-900 font-medium focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 focus:outline-none transition-all cursor-pointer"
                >
                  <option value="">All Types</option>
                  <option value="fully">Fully Furnished</option>
                  <option value="semi">Semi Furnished</option>
                  <option value="bare">Bare</option>
                </select>
              </div>
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3.5 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3 text-base md:text-lg group"
            >
              <Search className="w-5 h-5" />
              Speak to a Residential Rental Specialist
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Popular Requests */}
            <div className="mt-5 text-center">
              <span className="text-sm text-gray-500 mr-2">Clients often ask for:</span>
              {['Pet-Friendly Units', 'Parking Included', 'Fully Furnished', 'Near Schools'].map((feature) => (
                <span
                  key={feature}
                  className="inline-block px-4 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-full mr-2 mb-2"
                >
                  {feature}
                </span>
              ))}
            </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
